import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {SchemaInterfaceExtendingSchema} from 'constants/RegistryConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {
  FieldType,
  isRequiredLinkFolderItemType,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {assertNever, Optional} from 'util/Types';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {
  CalculateFinalConcentrationTransformationFieldInfo,
  SchemaInfoFields,
  SourceContentsTransformationFieldInfo,
  TransformationFieldInfo,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import {getSchemaInfoFieldKey, getTransformationFieldInfo} from './SchemaInfoFieldKeys';

export const transformationFieldInfoTypeOptions: {[type in TransformationFieldType]: string} = {
  CALCULATE_FINAL_CONCENTRATION: 'Calculate final concentration',
  PRIMARY_SAMPLE_ENTITY: 'Primary sample',
  DIRECT: 'Use column from output file',
  SOURCE_CONTENTS: 'Use contents of source container',
};

/** @package */
export interface SchemaInfoFieldsSectionProps {
  schema: Optional<{fields: Array<SchemaField>; allFields: Array<SchemaField>}>;
  fields: SchemaInfoFields;
  allowedFieldTypes: Array<TransformationFieldType>;
  allTagSchemas: Array<TagSchemaListingWithFieldsGraphQL>;
  allFieldsets: Array<SchemaInterfaceExtendingSchema>;
  /** Called with the field's key in `fields` (see getSchemaInfoFieldKey). */
  onUpdateField: (fieldKey: string, fieldInfo: TransformationFieldInfo | null) => void;
}

export default function SchemaInfoFieldsSection(props: SchemaInfoFieldsSectionProps): JSX.Element | null {
  if (!props.schema) {
    return null;
  }
  const fieldsToDisplay = props.schema.allFields.filter((field: SchemaField) => {
    const transformationFieldInfo = getTransformationFieldInfo(props.fields, field);
    return !isSchemaFieldArchived(field) || transformationFieldInfo != null;
  });
  // TODO notify if field is archived
  return (
    <>
      <Row className={css(styles.headerRow)}>
        <Col md={6} className={css(styles.headerColumn)}>
          Schema field
        </Col>
        <Col md={6} className={css(styles.headerColumn)}>
          Value
        </Col>
      </Row>
      {fieldsToDisplay.length === 0 ? (
        <div className={css(styles.column)}>This schema has no fields.</div>
      ) : null}
      {fieldsToDisplay.map((field: SchemaField) => {
        const fieldKey = getSchemaInfoFieldKey(props.fields, field);
        const transformationFieldInfo = getTransformationFieldInfo(props.fields, field);
        const tagSchemasForSourceContents = props.allTagSchemas.filter((schema) => {
          const requiredLink = field.requiredLink;
          if (isRequiredLinkTagSchema(requiredLink) && requiredLink.tagSchema) {
            return schema.id === requiredLink.tagSchema.id;
          } else if (isRequiredLinkFolderItemType(requiredLink) && requiredLink.folderItemType) {
            return schema.folderItemType === requiredLink.folderItemType;
          }
          return true;
        });
        const fieldsetsForSourceContents = props.allFieldsets.filter((fieldset) => {
          const requiredLink = field.requiredLink;
          if (isRequiredLinkSchemaInterface(requiredLink) && requiredLink.schemaInterface) {
            return fieldset.id === requiredLink.schemaInterface.id;
          }
          return true;
        });
        return (
          <Row key={field.id} className={css(styles.row)}>
            <Col md={6} className={css(styles.column)}>
              {field.displayName}
            </Col>
            <Col md={6} className={css(styles.column)}>
              <SelectDropdown
                displayName={
                  transformationFieldInfo
                    ? transformationFieldInfoTypeOptions[transformationFieldInfo.type]
                    : transformationFieldInfoTypeOptions[TransformationFieldType.DIRECT]
                }
                menu={
                  <Menu
                    value={transformationFieldInfo?.type}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                  >
                    {props.allowedFieldTypes.map((value) => {
                      switch (value) {
                        case TransformationFieldType.DIRECT:
                        case TransformationFieldType.PRIMARY_SAMPLE_ENTITY:
                          if (
                            value === TransformationFieldType.PRIMARY_SAMPLE_ENTITY &&
                            field.fieldType !== FieldType.BIOENTITY_LINK
                          ) {
                            return null;
                          }
                          return (
                            <Option
                              key={value}
                              value={value}
                              onSelect={() => {
                                props.onUpdateField(fieldKey, {type: value});
                              }}
                            >
                              {transformationFieldInfoTypeOptions[value]}
                            </Option>
                          );
                        case TransformationFieldType.SOURCE_CONTENTS:
                          if (field.fieldType !== FieldType.BIOENTITY_LINK) {
                            return null;
                          }
                          return (
                            <Option key={value} value={value}>
                              {transformationFieldInfoTypeOptions[value]}
                              <Menu
                                value={
                                  (transformationFieldInfo as SourceContentsTransformationFieldInfo)?.schema
                                }
                              >
                                <Option
                                  value={null}
                                  onSelect={() => {
                                    props.onUpdateField(fieldKey, {
                                      type: value,
                                    });
                                  }}
                                >
                                  All contents
                                </Option>
                                <SectionHeader>Schemas</SectionHeader>
                                {tagSchemasForSourceContents.map((schema) => (
                                  <Option
                                    key={schema.id}
                                    value={schema.id}
                                    onSelect={() => {
                                      props.onUpdateField(fieldKey, {
                                        type: value,
                                        schema: schema.id,
                                      });
                                    }}
                                  >
                                    {schema.name}
                                  </Option>
                                ))}
                                <SectionHeader>Fieldsets</SectionHeader>
                                {fieldsetsForSourceContents.map((schema) => (
                                  <Option
                                    key={schema.id}
                                    value={schema.id}
                                    onSelect={() => {
                                      props.onUpdateField(fieldKey, {
                                        type: value,
                                        schema: schema.id,
                                      });
                                    }}
                                  >
                                    {schema.name}
                                  </Option>
                                ))}
                              </Menu>
                            </Option>
                          );
                        case TransformationFieldType.CALCULATE_FINAL_CONCENTRATION:
                          if (field.fieldType !== FieldType.FLOAT) {
                            return null;
                          }
                          return (
                            <Option key={value} value={value}>
                              {transformationFieldInfoTypeOptions[value]}
                              <Menu
                                value={
                                  (
                                    transformationFieldInfo as CalculateFinalConcentrationTransformationFieldInfo
                                  )?.schema
                                }
                              >
                                <Option
                                  value={null}
                                  onSelect={() => {
                                    props.onUpdateField(fieldKey, {type: value});
                                  }}
                                >
                                  All contents
                                </Option>
                                {props.allTagSchemas.map((schema) => (
                                  <Option
                                    key={schema.id}
                                    value={schema.id}
                                    onSelect={() => {
                                      props.onUpdateField(fieldKey, {
                                        type: value,
                                        schema: schema.id,
                                      });
                                    }}
                                  >
                                    {schema.name}
                                  </Option>
                                ))}
                              </Menu>
                            </Option>
                          );
                        default:
                          assertAndContinue(
                            false,
                            `Unexpected transformationFieldInfo type: ${assertNever(value)}`
                          );
                          return null;
                      }
                    })}
                  </Menu>
                }
              />
            </Col>
          </Row>
        );
      })}
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    borderColor: GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  headerRow: {
    borderColor: GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderBottomWidth: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  column: {
    padding: '13px',
  },
  headerColumn: {
    marginBottom: '8px',
  },
});
